import React, { useState, useEffect, useRef } from "react";
import Popup from "reactjs-popup";
import CrmDropdown from "../../shared/components/CrmDropdown";
import { toastSuccess, toastError } from "../../../shared/toastHelper";

const axios = require("axios").default;

const modalContentStyle = {
  border: "none",
  borderRadius: "12px",
  boxShadow:
    "0 20px 24px -4px rgba(10,13,18,0.08), 0 8px 8px -4px rgba(10,13,18,0.03), 0 3px 3px -1.5px rgba(10,13,18,0.04)",
  padding: "0px",
  background: "var(--od-surface, #fff)",
  width: "480px",
  maxWidth: "95vw",
  minHeight: "50vh",
  maxHeight: "90vh",
  overflowY: "auto",
};

const overlayStyle = {
  zIndex: "4000",
  backgroundColor: "rgba(10, 13, 18, 0.7)",
};

const CompTicketsModal = ({ isOpen, onClose, team, csrfToken }) => {
  const [step, setStep] = useState(1);
  const [confirms, setConfirms] = useState([]);
  const [selectedConfirm, setSelectedConfirm] = useState(null);
  const [quantities, setQuantities] = useState({});
  const [ticketReservation, setTicketReservation] = useState(null);
  const [contactInfo, setContactInfo] = useState({
    firstName: "",
    lastName: "",
    email: "",
    sendTicketEmail: true,
  });
  const [loading, setLoading] = useState(false);
  const [fetchingConfirms, setFetchingConfirms] = useState(false);
  const ticketReservationRef = useRef(null);

  useEffect(() => {
    ticketReservationRef.current = ticketReservation;
  }, [ticketReservation]);

  useEffect(() => {
    if (isOpen) {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      setFetchingConfirms(true);
      axios
        .get(`/teams/${team.id}/confirms/upcoming_with_ticket_types.json`)
        .then((response) => {
          setConfirms(response.data);
        })
        .catch(() => {
          toastError("Failed to load events");
        })
        .finally(() => {
          setFetchingConfirms(false);
        });
    }
  }, [isOpen, team.id, csrfToken]);

  const resetState = () => {
    setStep(1);
    setConfirms([]);
    setSelectedConfirm(null);
    setQuantities({});
    setTicketReservation(null);
    setContactInfo({
      firstName: "",
      lastName: "",
      email: "",
      sendTicketEmail: true,
    });
    setLoading(false);
    setFetchingConfirms(false);
  };

  const cancelReservation = () => {
    const reservation = ticketReservationRef.current;
    if (reservation) {
      axios
        .delete(`/teams/${team.id}/manual_ticket_reservations/${reservation.token}`)
        .catch(() => {});
    }
  };

  const handleClose = () => {
    cancelReservation();
    resetState();
    onClose();
  };

  const handleConfirmSelect = (confirmId) => {
    const found = confirms.find((c) => c.id === confirmId);
    setSelectedConfirm(found || null);
    setQuantities({});
  };

  const handleQuantityChange = (ticketTypeId, value, max) => {
    const qty = parseInt(value, 10);
    const clamped = isNaN(qty) || qty < 0 ? 0 : Math.min(qty, max);
    setQuantities((prev) => ({
      ...prev,
      [ticketTypeId]: clamped,
    }));
  };

  const totalTickets = Object.values(quantities).reduce(
    (sum, q) => sum + (q || 0),
    0
  );

  const canSubmitStep1 = selectedConfirm && totalTickets > 0;

  const buildOrderItems = () => {
    if (!selectedConfirm) return [];
    return selectedConfirm.ticket_types
      .filter((tt) => quantities[tt.id] > 0)
      .map((tt) => ({
        ticket_type_id: tt.id,
        quantity: quantities[tt.id],
        face_value: parseFloat(tt.price) || 0,
      }));
  };

  const handleNextStep = async () => {
    if (!canSubmitStep1) return;
    setLoading(true);

    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    const payload = {
      ticket_reservation: {
        calendar_event_id: selectedConfirm.id,
        complimentary: true,
        manual_type: "Complimentary",
        order_items_attributes: buildOrderItems(),
      },
    };

    try {
      await axios.post(
        `/teams/${team.id}/manual_ticket_reservations/build`,
        payload
      );

      const createResponse = await axios.post(
        `/teams/${team.id}/manual_ticket_reservations`,
        payload
      );

      setTicketReservation(createResponse.data);
      setStep(2);
    } catch (error) {
      const messages = error.response?.data;
      if (Array.isArray(messages)) {
        toastError(messages.join(", "));
      } else {
        toastError(messages || "Failed to create reservation");
      }
    } finally {
      setLoading(false);
    }
  };

  const canSubmitStep2 =
    contactInfo.firstName.trim().length > 0 &&
    contactInfo.lastName.trim().length > 0 &&
    contactInfo.email.trim().length > 0;

  const handleComplete = async () => {
    if (!canSubmitStep2 || !ticketReservation) return;
    setLoading(true);

    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    const token = ticketReservation.token;

    try {
      await axios.patch(
        `/teams/${team.id}/manual_ticket_reservations/${token}`,
        {
          ticket_reservation: {
            first_name: contactInfo.firstName,
            last_name: contactInfo.lastName,
            email: contactInfo.email,
            should_send_ticket_email: contactInfo.sendTicketEmail,
            cash_paid: ticketReservation.total,
          },
        }
      );

      await axios.get(
        `/teams/${team.id}/manual_ticket_reservations/${token}/complete`,
        { headers: { Accept: "application/json" } }
      );

      setTicketReservation(null);
      toastSuccess("Comp tickets created successfully");
      resetState();
      onClose();
    } catch (error) {
      const messages = error.response?.data;
      if (Array.isArray(messages)) {
        toastError(messages.join(", "));
      } else {
        toastError(messages || "Failed to complete reservation");
      }
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "24px 24px 20px 24px" }}>
        <h2
          style={{ fontSize: "18px", fontWeight: "600", color: "var(--od-c-333333, #333)", margin: "0" }}
        >
          Comp Tickets
        </h2>
      </div>

      <div style={{ padding: "0 24px" }}>
        <div style={{ marginBottom: "16px" }}>
          <label
            className="d-block mb-1"
            style={{ fontSize: "14px", fontWeight: "500", color: "var(--od-c-555555, #555)" }}
          >
            Event
          </label>
          <CrmDropdown
            value={selectedConfirm?.id || null}
            options={confirms.map((c) => {
              const parts = [];
              if (c.venue_name) parts.push(c.venue_name);
              if (c.start_date) {
                const d = new Date(c.start_date + "T00:00:00");
                parts.push(d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }));
              }
              return {
                value: c.id,
                label: c.title,
                subtitle: parts.join(" \u00B7 "),
              };
            })}
            onChange={handleConfirmSelect}
            placeholder={fetchingConfirms ? "Loading events..." : "Select an event"}
            enableSearch={confirms.length > 5}
            minWidth="100%"
            ariaLabel="Select an event"
          />
        </div>

        {selectedConfirm && (
          <div>
            <label
              className="d-block mb-2"
              style={{ fontSize: "14px", fontWeight: "500", color: "var(--od-c-555555, #555)" }}
            >
              Tickets
            </label>
            {selectedConfirm.ticket_types.length === 0 ? (
              <p style={{ fontSize: "14px", color: "var(--od-c-888888, #888)" }}>
                No ticket types available for this event.
              </p>
            ) : (
              selectedConfirm.ticket_types.map((tt) => (
                <div key={tt.id} className="comp-tickets-ticket-row">
                  <div className="comp-tickets-ticket-info">
                    <div className="comp-tickets-ticket-name">{tt.name}</div>
                    <div className="comp-tickets-ticket-avail">
                      {tt.available} available
                    </div>
                  </div>
                  <input
                    type="number"
                    className="comp-tickets-quantity-input"
                    min="0"
                    max={tt.available}
                    value={quantities[tt.id] || 0}
                    onChange={(e) =>
                      handleQuantityChange(tt.id, e.target.value, tt.available)
                    }
                  />
                </div>
              ))
            )}
          </div>
        )}
      </div>

      <div
        style={{
          padding: "24px",
          display: "flex",
          gap: "12px",
        }}
      >
        <button
          onClick={handleClose}
          className="btn btn-setlist-secondary"
          style={{ flex: 1 }}
        >
          Cancel
        </button>
        <button
          onClick={handleNextStep}
          disabled={!canSubmitStep1 || loading}
          className="btn btn-setlist-primary"
          style={{ flex: 1 }}
        >
          {loading ? "Loading..." : "Next"}
        </button>
      </div>
    </div>
  );

  const renderStep2 = () => {
    const selectedItems = selectedConfirm
      ? selectedConfirm.ticket_types.filter((tt) => quantities[tt.id] > 0)
      : [];

    return (
      <div style={{ display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "24px 24px 20px 24px" }}>
          <h2
            style={{ fontSize: "18px", fontWeight: "600", color: "var(--od-c-333333, #333)", margin: "0" }}
          >
            Contact Info
          </h2>
        </div>

        <div style={{ padding: "0 24px" }}>
          <div className="comp-tickets-step-summary">
            <p style={{ fontWeight: "500" }}>{selectedConfirm?.title}</p>
            {selectedItems.map((tt) => (
              <p key={tt.id}>
                {tt.name} x {quantities[tt.id]}
              </p>
            ))}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <div>
              <label
                className="d-block mb-1"
                style={{ fontSize: "14px", fontWeight: "500", color: "var(--od-c-555555, #555)" }}
              >
                First Name
              </label>
              <input
                type="text"
                className="form-control"
                value={contactInfo.firstName}
                onChange={(e) =>
                  setContactInfo((prev) => ({
                    ...prev,
                    firstName: e.target.value,
                  }))
                }
              />
            </div>
            <div>
              <label
                className="d-block mb-1"
                style={{ fontSize: "14px", fontWeight: "500", color: "var(--od-c-555555, #555)" }}
              >
                Last Name
              </label>
              <input
                type="text"
                className="form-control"
                value={contactInfo.lastName}
                onChange={(e) =>
                  setContactInfo((prev) => ({
                    ...prev,
                    lastName: e.target.value,
                  }))
                }
              />
            </div>
            <div>
              <label
                className="d-block mb-1"
                style={{ fontSize: "14px", fontWeight: "500", color: "var(--od-c-555555, #555)" }}
              >
                Email
              </label>
              <input
                type="email"
                className="form-control"
                value={contactInfo.email}
                onChange={(e) =>
                  setContactInfo((prev) => ({
                    ...prev,
                    email: e.target.value,
                  }))
                }
              />
            </div>
            <div className="d-flex align-items-center">
              <input
                type="checkbox"
                id="sendTicketEmail"
                checked={contactInfo.sendTicketEmail}
                onChange={(e) =>
                  setContactInfo((prev) => ({
                    ...prev,
                    sendTicketEmail: e.target.checked,
                  }))
                }
                style={{ marginRight: "8px" }}
              />
              <label
                htmlFor="sendTicketEmail"
                style={{ fontSize: "14px", color: "var(--od-c-555555, #555)", margin: 0 }}
              >
                Send ticket email
              </label>
            </div>
          </div>
        </div>

        <div
          style={{
            padding: "24px",
            display: "flex",
            gap: "12px",
          }}
        >
          <button
            onClick={() => setStep(1)}
            className="btn btn-setlist-secondary"
            style={{ flex: 1 }}
            disabled={loading}
          >
            Back
          </button>
          <button
            onClick={handleComplete}
            disabled={!canSubmitStep2 || loading}
            className="btn btn-setlist-primary"
            style={{ flex: 1 }}
          >
            {loading ? "Completing..." : "Complete"}
          </button>
        </div>
      </div>
    );
  };

  return (
    <Popup
      open={isOpen}
      onClose={handleClose}
      contentStyle={modalContentStyle}
      overlayStyle={overlayStyle}
      closeOnDocumentClick={false}
      closeOnEscape
    >
      {(_close) => (step === 1 ? renderStep1() : renderStep2())}
    </Popup>
  );
};

export default CompTicketsModal;
